import { api } from "@/lib/api";

let scriptPromise = null;

/** Load Razorpay checkout.js once. */
export function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load Razorpay. Check your connection and try again."));
    };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Start a Razorpay payment for a WhatsApp Automation plan.
 * @returns {Promise<{status:string, order?:object}>}
 * Resolves with { status: 'success', order } on verified payment.
 * Rejects with err where err.code may be 'disabled' | 'dismissed' | 'failed'.
 */
export async function startPlanPayment({ planKey, period, businessName = "E Telecom", description }) {
  // 1) Create order server-side (amount resolved on the server).
  let order;
  try {
    const { data } = await api.post("/payments/create-order", { plan_key: planKey, period });
    order = data;
  } catch (e) {
    const status = e?.response?.status;
    if (status === 503) {
      const err = new Error(e?.response?.data?.detail || "Online payments are not available yet.");
      err.code = "disabled";
      throw err;
    }
    const err = new Error(e?.response?.data?.detail || "Could not start the payment.");
    err.code = "failed";
    throw err;
  }

  // 2) Ensure checkout script is available.
  await loadRazorpayScript();

  // 3) Open checkout and await the result.
  return new Promise((resolve, reject) => {
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency || "INR",
      name: businessName,
      description: description || `${order.plan_name} — ${order.period_label}`,
      order_id: order.order_id,
      theme: { color: "#0A1B33" },
      handler: async (response) => {
        try {
          const { data } = await api.post("/payments/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          resolve({ status: "success", order: data.order, payment_id: response.razorpay_payment_id });
        } catch (e) {
          const err = new Error(e?.response?.data?.detail || "Payment verification failed.");
          err.code = "failed";
          reject(err);
        }
      },
      modal: {
        ondismiss: () => {
          const err = new Error("Payment cancelled.");
          err.code = "dismissed";
          reject(err);
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      const err = new Error(resp?.error?.description || "Payment failed.");
      err.code = "failed";
      reject(err);
    });
    rzp.open();
  });
}
